// The in-memory API's skill packs (M4-02; services/api/src/routes/agents/skill-packs.ts), with
// the real routes' rules: behind the `agents` add-on (which bundles `memory`; 403
// feature_disabled otherwise), readers list and open, managers assign a pack to roles (422 for a
// role the company does not have, 409 on a deprecated pack, the same set is a no-op), roles are
// stored sorted, and the detail lists the DEs whose seat holds one of the roles. WF-CONTROL:
// `confirm_assignees` refuses (409) a role change that moves a DE not in the list; an add on an
// archived pack is 409 unless the patch restores it; a change of roles or archive state appends a
// `skills` profile revision for each DE whose skills changed (fake-control.ts `reviseSkills`).
//
// The packs are the real core packs: their files are read from
// `packages/domain-agents/skill-packs/core` at build time, and each file's sha256 and the pack's
// content hash are computed here the way `skillPackContentHash` computes them (the sorted
// `sha256sum` listing, hashed). `fake-skill-packs-contract.test.ts` checks the hashes against
// `core.lock.json`, so the fake cannot drift from what the seed publishes.
import { SkillAdditionsPut, SkillChangeRequestCreate, SkillKey } from "@companyos/schema/contracts";
import type { OperationOutput, RouteId } from "@companyos/sdk";
import {
  featureDisabled,
  type Handler,
  json,
  notFound,
  type Principal,
  page,
  problem,
  validation,
} from "./fake";
import type { FakeCompany, FakeDe } from "./fake-data";
import { uid } from "./fake-data";
import type { Role, Seat, SeatAssignment } from "./types";

type Detail = OperationOutput<"skillPacks.get">;
type Pack = Omit<Detail, "assignees">;
type Manifest = Pack["manifest"];

const RAW = import.meta.glob("../../../../packages/domain-agents/skill-packs/core/**/*", {
  query: "?raw",
  import: "default",
  eager: true,
}) as Record<string, string>;

const EPOCH = "2026-06-01T09:00:00.000Z";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const forbidden = () => problem(403, "forbidden", "Forbidden", "managers only");

// sha256 (FIPS 180-4), synchronous: the fake's handlers answer synchronously. --------------------

const K = Uint32Array.from([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

export function sha256Hex(bytes: Uint8Array): string {
  const bitLen = bytes.length * 8;
  const padded = new Uint8Array(Math.ceil((bytes.length + 9) / 64) * 64);
  padded.set(bytes);
  padded[bytes.length] = 0x80;
  const view = new DataView(padded.buffer);
  view.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000));
  view.setUint32(padded.length - 4, bitLen >>> 0);
  const h = Uint32Array.from([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);
  const rotr = (x: number, n: number) => (x >>> n) | (x << (32 - n));
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const a = w[i - 15] as number;
      const b = w[i - 2] as number;
      const s0 = rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3);
      const s1 = rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10);
      w[i] = ((w[i - 16] as number) + s0 + (w[i - 7] as number) + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = h as unknown as number[];
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e as number, 6) ^ rotr(e as number, 11) ^ rotr(e as number, 25);
      const ch = ((e as number) & (f as number)) ^ (~(e as number) & (g as number));
      const t1 = ((hh as number) + S1 + ch + (K[i] as number) + (w[i] as number)) >>> 0;
      const S0 = rotr(a as number, 2) ^ rotr(a as number, 13) ^ rotr(a as number, 22);
      const maj =
        ((a as number) & (b as number)) ^
        ((a as number) & (c as number)) ^
        ((b as number) & (c as number));
      const t2 = (S0 + maj) >>> 0;
      hh = g;
      g = f;
      f = e;
      e = ((d as number) + t1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (t1 + t2) >>> 0;
    }
    const out = [a, b, c, d, e, f, g, hh] as number[];
    for (let i = 0; i < 8; i++) h[i] = ((h[i] as number) + (out[i] as number)) >>> 0;
  }
  return Array.from(h, (x) => x.toString(16).padStart(8, "0")).join("");
}

const utf8 = new TextEncoder();

/** The core packs as the catalog holds them: files sorted by path, hashes computed. */
export type CorePackData = {
  key: string;
  manifest: Manifest;
  files: Pack["files"];
  content_hash: string;
};

function corePackData(): CorePackData[] {
  const byKey = new Map<string, { path: string; text: string }[]>();
  for (const [file, text] of Object.entries(RAW)) {
    const rel = file.split("/skill-packs/core/")[1];
    if (!rel) continue;
    const slash = rel.indexOf("/");
    const key = rel.slice(0, slash);
    const list = byKey.get(key) ?? [];
    list.push({ path: rel.slice(slash + 1), text });
    byKey.set(key, list);
  }
  return [...byKey.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([key, list]) => {
      const files = list
        .sort((a, b) => (a.path < b.path ? -1 : a.path > b.path ? 1 : 0))
        .map((f) => {
          const bytes = utf8.encode(f.text);
          return { path: f.path, bytes: bytes.byteLength, sha256: sha256Hex(bytes), text: f.text };
        });
      const listing = files.map((f) => `${f.sha256}  ${f.path}\n`).join("");
      const manifestText = files.find((f) => f.path === "manifest.json")?.text ?? "{}";
      return {
        key,
        manifest: JSON.parse(manifestText) as Manifest,
        files,
        content_hash: sha256Hex(utf8.encode(listing)),
      };
    });
}

let core: CorePackData[] | null = null;
/** Computed once, on first use (the fake's other routes never pay for it). */
export const fakeCorePacks = (): CorePackData[] => {
  core ??= corePackData();
  return core;
};

const companyNo = (id: string) => Number.parseInt(id.slice(-4), 16) % 4096;

export function createSkillPackHandlers(deps: {
  now: () => Date;
  featureOn: (p: Principal, feature: string) => boolean;
  rolesOf: (companyId: string) => Role[];
  seatsOf: (company: FakeCompany) => Seat[];
  assignmentsOf: (company: FakeCompany) => SeatAssignment[];
  desOf: (companyId: string) => FakeDe[];
  /** WF-CONTROL: a DE's skills changed (fake-control.ts `reviseSkills`). */
  reviseSkills?: (p: Principal, deId: string, from: string[], to: string[]) => void;
  /**
   * SKILL-OVERRIDES: additions already written in every company, by key. `stale_default`: they
   * were written against an older default (version and text), so the skill shows "Default
   * updated".
   */
  seedAdditions?: Record<
    string,
    { text: string; stale_default?: { version: string; text: string } }
  >;
}): Partial<Record<RouteId, Handler>> {
  const store = new Map<string, Pack[]>();

  /** The platform catalog's copies, as `skill_pack.mirror` writes them (every company). */
  const packsOf = (companyId: string): Pack[] => {
    let list = store.get(companyId);
    if (!list) {
      list = fakeCorePacks().map((c, i) => ({
        company_id: companyId,
        id: uid(0x5c00, companyNo(companyId) * 16 + i + 1),
        scope: "platform",
        source_pack_id: uid(0x5b00, i + 1),
        key: c.key,
        name: c.manifest.name,
        description: c.manifest.description,
        version: c.manifest.version,
        format: "herk-skill",
        content_hash: c.content_hash,
        object_key: `skill-packs/${c.key}@${c.content_hash}`,
        manifest: c.manifest,
        source_ref: c.manifest.source_ref,
        license_note: c.manifest.license_note,
        files: c.files,
        status: "published",
        published_at: EPOCH,
        deprecated_at: null,
        role_ids: [],
        last_used_at: null,
        archived_at: null,
        actor_kind: "system",
        actor_membership_id: null,
        created_at: EPOCH,
        updated_at: EPOCH,
      }));
      store.set(companyId, list);
    }
    return list;
  };

  /** D15: the memory-discipline pack exists while the discipline is on (the default). */
  const visible = (p: Principal, pack: Pack) =>
    pack.key !== "memory-discipline" ||
    p.company.settings.memory?.agent_discipline?.enabled !== false;

  /** Gate, then the principal; null = answered already. */
  const gate = (p: Principal | null): Response | null => {
    if (!p) return notFound();
    if (!deps.featureOn(p, "agents") || !deps.featureOn(p, "memory"))
      return featureDisabled("agents");
    return null;
  };

  /** The DEs on a seat of one of `roleIds` now (production's `assigneesOf`). */
  const deSeats = (p: Principal, roleIds?: ReadonlySet<string>) => {
    const seats = new Map(deps.seatsOf(p.company).map((s) => [s.id, s]));
    const out: { membership_id: string; role_id: string }[] = [];
    for (const a of deps.assignmentsOf(p.company)) {
      if (a.active_to !== null || a.membership_kind !== "digital_employee") continue;
      const role = seats.get(a.seat_id)?.role_id;
      if (!role || (roleIds && !roleIds.has(role))) continue;
      out.push({ membership_id: a.membership_id, role_id: role });
    }
    return out;
  };

  /**
   * Each DE's skills: the packs its seats' runs mount (production's `packsForSeat`: published,
   * unarchived, assigned to the seat's role, plus D15's discipline pack while it is on).
   */
  const skillsByDe = (p: Principal): Map<string, string[]> => {
    const packs = packsOf(p.company.id).filter(
      (k) => k.status === "published" && k.archived_at === null && visible(p, k),
    );
    const out = new Map<string, Set<string>>();
    for (const a of deSeats(p)) {
      const keys = out.get(a.membership_id) ?? new Set<string>();
      for (const k of packs)
        if (k.key === "memory-discipline" || k.role_ids.includes(a.role_id)) keys.add(k.key);
      out.set(a.membership_id, keys);
    }
    return new Map([...out].map(([id, keys]) => [id, [...keys].sort()]));
  };

  const detail = (p: Principal, pack: Pack): Detail => {
    const roles = new Set(pack.role_ids);
    const seats = deps.seatsOf(p.company).filter((s) => s.role_id && roles.has(s.role_id));
    const des = new Map(deps.desOf(p.company.id).map((d) => [d.membership_id, d]));
    const assignees: Detail["assignees"] = [];
    for (const a of deps.assignmentsOf(p.company)) {
      if (a.active_to !== null || a.membership_kind !== "digital_employee") continue;
      const seat = seats.find((s) => s.id === a.seat_id);
      const de = des.get(a.membership_id);
      if (!seat?.role_id || !de) continue;
      assignees.push({
        membership_id: de.membership_id,
        display_name: de.display_name,
        seat_id: seat.id,
        role_id: seat.role_id,
      });
    }
    assignees.sort((x, y) =>
      x.display_name === y.display_name
        ? x.membership_id.localeCompare(y.membership_id)
        : x.display_name.localeCompare(y.display_name),
    );
    return { ...pack, assignees };
  };

  // SKILL-OVERRIDES (services/api/src/domain/skill-packs/additions.ts): a company's additions to
  // one of our skills, versioned, layered on the published pack of that key. A key with no pack
  // is a self-hosted skill: view only, "Request a change" (202), editing it is 409.
  type Version = OperationOutput<"skills.get">["versions"][number] & { base_hash: string };
  const additions = new Map<string, Map<string, Version[]>>();
  /** Old defaults the seeded additions were written against, by content hash. */
  const oldDefaults = new Map<string, string>();
  const versionsOf = (companyId: string, key: string): Version[] => {
    let byKey = additions.get(companyId);
    if (!byKey) {
      byKey = new Map();
      for (const [k, s] of Object.entries(deps.seedAdditions ?? {})) {
        const pack = packsOf(companyId).find((r) => r.key === k);
        if (!pack) continue;
        const hash = s.stale_default
          ? sha256Hex(utf8.encode(s.stale_default.text))
          : pack.content_hash;
        if (s.stale_default) oldDefaults.set(hash, s.stale_default.text);
        byKey.set(k, [
          {
            n: 1,
            text: s.text,
            via: "edit",
            base_version: s.stale_default?.version ?? pack.version,
            base_hash: hash,
            actor_membership_id: null,
            created_at: EPOCH,
          },
        ]);
      }
      additions.set(companyId, byKey);
    }
    let list = byKey.get(key);
    if (!list) {
      list = [];
      byKey.set(key, list);
    }
    return list;
  };
  const defaultOf = (p: Principal, key: string) =>
    packsOf(p.company.id).find((r) => r.key === key && r.status === "published" && visible(p, r));
  const skillText = (pack: Pack) =>
    pack.files.find((f) => f.path === "SKILL.md")?.text ??
    pack.files
      .filter((f) => f.path !== "manifest.json")
      .map((f) => f.text)
      .join("\n\n");
  const stateOf = (p: Principal, key: string) => {
    const pack = defaultOf(p, key);
    const cur = versionsOf(p.company.id, key)[0] ?? null;
    const has = !!pack && !!cur?.text;
    return {
      key,
      hosting: pack ? ("companyos" as const) : ("self_hosted" as const),
      default_version: pack?.version ?? null,
      n: cur?.n ?? 0,
      has_additions: has,
      default_updated: has && cur?.base_hash !== pack?.content_hash,
    };
  };
  const skillDetail = (p: Principal, key: string): OperationOutput<"skills.get"> => {
    const pack = defaultOf(p, key);
    const list = pack ? versionsOf(p.company.id, key) : [];
    const cur = list[0] ?? null;
    const state = stateOf(p, key);
    return {
      ...state,
      default: pack
        ? {
            pack_id: pack.id,
            name: pack.name,
            version: pack.version,
            content_hash: pack.content_hash,
            text: skillText(pack),
          }
        : null,
      additions: pack ? (cur?.text ?? null) : null,
      base_version: cur?.base_version ?? null,
      previous_default_text:
        state.default_updated && cur ? (oldDefaults.get(cur.base_hash) ?? null) : null,
      versions: list.slice(0, 20).map(({ base_hash: _h, ...v }) => v),
      can_edit: p.manager,
    };
  };
  const keyParam = (params: Record<string, string>) => {
    try {
      return decodeURIComponent(params.key ?? "").trim();
    } catch {
      return "";
    }
  };

  const skillHandlers: Partial<Record<RouteId, Handler>> = {
    "skills.list": ({ principal: p }) => {
      const off = gate(p);
      if (off || !p) return off ?? notFound();
      const keys = new Set([
        ...packsOf(p.company.id)
          .filter((r) => r.status === "published" && visible(p, r))
          .map((r) => r.key),
        ...[...(additions.get(p.company.id)?.entries() ?? [])]
          .filter(([, v]) => v.length > 0)
          .map(([k]) => k),
      ]);
      // The DEs whose seat's packs include each skill (a role's skill-pack assignment).
      const mounted = new Map<string, string[]>();
      for (const [de, ks] of skillsByDe(p))
        for (const k of ks) mounted.set(k, [...(mounted.get(k) ?? []), de]);
      return json({
        data: [...keys]
          .sort()
          .map((k) => ({ ...stateOf(p, k), mounted_by: [...(mounted.get(k) ?? [])].sort() })),
      });
    },
    "skills.get": ({ principal: p, params }) => {
      const off = gate(p);
      if (off || !p) return off ?? notFound();
      const key = keyParam(params);
      if (!SkillKey.safeParse(key).success) return validation("key: 1 to 80 characters");
      return json(skillDetail(p, key));
    },
    "skills.additions.put": ({ principal: p, params, body }) => {
      const off = gate(p);
      if (off || !p) return off ?? notFound();
      if (!p.manager) return forbidden();
      const key = keyParam(params);
      if (!SkillKey.safeParse(key).success) return validation("key: 1 to 80 characters");
      const parsed = SkillAdditionsPut.safeParse(body);
      if (!parsed.success) return validation(parsed.error.issues[0]?.message ?? "invalid body");
      const b = parsed.data;
      const pack = defaultOf(p, key);
      if (!pack)
        return problem(
          409,
          "conflict",
          "Conflict",
          "this skill lives in a self-hosted runtime: request a change instead of editing it here",
        );
      const list = versionsOf(p.company.id, key);
      const cur = list[0] ?? null;
      if ((cur?.n ?? 0) !== b.expected_n)
        return problem(
          409,
          "conflict",
          "Conflict",
          "the additions changed since you opened them: look again",
        );
      if (b.default_hash !== undefined && b.default_hash !== pack.content_hash)
        return problem(
          409,
          "conflict",
          "Conflict",
          "we shipped a new default since you opened it: look again",
        );
      const text = b.text?.trim() ? b.text.trim() : null;
      const same = (cur?.text ?? null) === text;
      const via =
        text === null
          ? cur?.text
            ? "reset"
            : null
          : !same
            ? "edit"
            : cur?.base_hash !== pack.content_hash
              ? "review"
              : null;
      if (via)
        list.unshift({
          n: (cur?.n ?? 0) + 1,
          text,
          via,
          base_version: pack.version,
          base_hash: pack.content_hash,
          actor_membership_id: null,
          created_at: deps.now().toISOString(),
        });
      return json(skillDetail(p, key));
    },
    "skills.changeRequest": ({ principal: p, params, body }) => {
      const off = gate(p);
      if (off || !p) return off ?? notFound();
      if (!p.manager) return forbidden();
      const key = keyParam(params);
      if (!SkillKey.safeParse(key).success) return validation("key: 1 to 80 characters");
      if (!SkillChangeRequestCreate.safeParse(body).success)
        return validation("what: 1 to 400 characters");
      if (defaultOf(p, key))
        return problem(
          409,
          "conflict",
          "Conflict",
          "this skill is ours: add your company's lines to it instead",
        );
      return json({ accepted: true }, 202);
    },
  };

  return {
    ...skillHandlers,
    "skillPacks.list": ({ principal: p, url }) => {
      const off = gate(p);
      if (off || !p) return off ?? notFound();
      const status = url.searchParams.get("status");
      const role = url.searchParams.get("role_id");
      const rows = packsOf(p.company.id)
        .filter((r) => visible(p, r))
        .filter((r) => !status || r.status === status)
        .filter((r) => !role || r.role_ids.includes(role))
        .map(({ files, ...r }) => ({
          ...r,
          files: files.map(({ text: _text, ...f }) => f),
        }));
      return json(page(rows, url));
    },

    "skillPacks.get": ({ principal: p, params }) => {
      const off = gate(p);
      if (off || !p) return off ?? notFound();
      const pack = packsOf(p.company.id).find((r) => r.id === params.id && visible(p, r));
      return pack ? json(detail(p, pack)) : notFound();
    },

    "skillPacks.update": ({ principal: p, params, body }) => {
      const off = gate(p);
      if (off || !p) return off ?? notFound();
      if (!p.manager) return forbidden();
      const pack = packsOf(p.company.id).find((r) => r.id === params.id && visible(p, r));
      if (!pack) return notFound();
      const b = (body ?? {}) as {
        role_ids?: unknown;
        add_role_ids?: unknown;
        remove_role_ids?: unknown;
        archived?: unknown;
        confirm_assignees?: unknown;
        require_assignees?: unknown;
      };
      const keys = Object.keys(b);
      const FIELDS = [
        "role_ids",
        "add_role_ids",
        "remove_role_ids",
        "archived",
        "confirm_assignees",
        "require_assignees",
      ];
      if (keys.some((k) => !FIELDS.includes(k))) return validation("unknown field");
      if (keys.length === 0) return validation("nothing to change");
      if (b.archived !== undefined && typeof b.archived !== "boolean")
        return validation("archived: a boolean");
      if (
        b.role_ids !== undefined &&
        (b.add_role_ids !== undefined || b.remove_role_ids !== undefined)
      )
        return validation("role_ids replaces the set: send it, or add_role_ids / remove_role_ids");
      const confirm = b.confirm_assignees;
      if (
        confirm !== undefined &&
        !(
          Array.isArray(confirm) &&
          confirm.length <= 500 &&
          confirm.every((r) => typeof r === "string" && UUID.test(r))
        )
      )
        return validation("confirm_assignees: up to 500 membership ids");
      const require = b.require_assignees;
      if (
        require !== undefined &&
        !(
          Array.isArray(require) &&
          require.length <= 50 &&
          require.every((r) => typeof r === "string" && UUID.test(r))
        )
      )
        return validation("require_assignees: up to 50 membership ids");
      const idList = (v: unknown, min: number) =>
        Array.isArray(v) &&
        v.length >= min &&
        v.length <= 64 &&
        v.every((r) => typeof r === "string");
      // Everything is checked before anything changes (the API's request is one transaction).
      let want: string[] | null = null;
      if (b.role_ids !== undefined) {
        if (!idList(b.role_ids, 0)) return validation("role_ids: up to 64 role ids");
        const ids = b.role_ids as string[];
        if (new Set(ids).size !== ids.length) return validation("role_ids: duplicate role id");
        want = [...ids];
      }
      // WF-CONTROL: add or remove some roles, keeping the others.
      if (b.add_role_ids !== undefined || b.remove_role_ids !== undefined) {
        if (b.add_role_ids !== undefined && !idList(b.add_role_ids, 1))
          return validation("add_role_ids: 1 to 64 role ids");
        if (b.remove_role_ids !== undefined && !idList(b.remove_role_ids, 1))
          return validation("remove_role_ids: 1 to 64 role ids");
        const drop = new Set((b.remove_role_ids as string[] | undefined) ?? []);
        want = [
          ...new Set([...pack.role_ids, ...((b.add_role_ids as string[] | undefined) ?? [])]),
        ].filter((r) => !drop.has(r));
      }
      // WF-CONTROL: assigning a role (via role_ids or add_role_ids) assigns a pack that mounts: an
      // archived pack gains no role unless the same patch restores it.
      const added = (want ?? []).filter((r) => !pack.role_ids.includes(r));
      if (added.length > 0 && pack.archived_at !== null && b.archived !== false)
        return problem(
          409,
          "conflict",
          "Conflict",
          "the pack is archived: restore it first (archived: false)",
        );
      // WF-CONTROL: the DEs the caller confirmed; anyone else on a role that moves is a change
      // nobody confirmed (409).
      if ((Array.isArray(confirm) || require !== undefined) && want !== null) {
        const next = want;
        const moved = new Set(
          [...new Set([...pack.role_ids, ...next])].filter(
            (r) => pack.role_ids.includes(r) !== next.includes(r),
          ),
        );
        const holders = deSeats(p, moved);
        if (Array.isArray(confirm)) {
          const ok = new Set(confirm as string[]);
          const unconfirmed = holders.filter((a) => !ok.has(a.membership_id));
          if (unconfirmed.length > 0)
            return problem(
              409,
              "conflict",
              "Conflict",
              `${unconfirmed.length} other digital employee(s) now share the role: confirm again`,
            );
        }
        // `require_assignees`: each listed DE must still hold a seat of a role that moves.
        if (require !== undefined) {
          const held = new Set(holders.map((a) => a.membership_id));
          if ((require as string[]).some((id) => !held.has(id)))
            return problem(
              409,
              "conflict",
              "Conflict",
              "the digital employee no longer holds that role: look again",
            );
        }
      }
      if (want !== null) {
        const known = new Set(deps.rolesOf(p.company.id).map((r) => r.id));
        if (want.some((r) => !known.has(r)))
          return validation("role_ids: every role must be one of the company's roles");
        want = [...want].sort();
      }
      const roles =
        want !== null &&
        !(want.length === pack.role_ids.length && want.every((r, i) => r === pack.role_ids[i]));
      // AGENT-SAFETY: archive / restore (kept, never deleted), as the API's skillPacks.update
      const archive = typeof b.archived === "boolean" && (pack.archived_at !== null) !== b.archived;
      if ((roles || archive) && pack.status === "deprecated")
        return problem(409, "conflict", "Conflict", "a deprecated pack cannot be changed");
      const at = deps.now().toISOString();
      const before = roles || archive ? skillsByDe(p) : null;
      if (archive) {
        pack.archived_at = b.archived ? at : null;
        if (!b.archived) pack.last_used_at = at;
      }
      if (roles && want) {
        pack.role_ids = want;
        pack.updated_at = at;
      }
      // WF-CONTROL: a `skills` revision for each DE whose skills changed (skill-revisions.ts).
      if (before) {
        const after = skillsByDe(p);
        for (const [id, from] of before) deps.reviseSkills?.(p, id, from, after.get(id) ?? []);
      }
      return json(detail(p, pack));
    },
  };
}
